import { type App, type ComputedRef, type Ref } from 'vue'
import { computed, inject, ref, toRef } from 'vue'
import type { RegionPluginFragment } from '@/graphql/api'
import { RegionCode } from '@/graphql/api'
import type { AppTranslator, Locale } from '@/i18n'
import { type ReactiveCookiePlugin } from '@/plugins/cookie-plugin'
import { includes, isNotUndefinedOrNull } from '@/helpers'
import type { SelectOption } from '@/components/ui/inputs/th-select.ts'

export type SelectRegionFunction = (regionCode: RegionCode) => Promise<void>

const pluginKey = 'app.plugin.region'

export type RoutingRegion =
    | Lowercase<Exclude<RegionCode, typeof noPrefixRegion>>
    | ''
export type MonolithLegacyRegionCookie = Lowercase<RegionCode>

export type RegionFragmentMap = Record<
    RegionCode,
    RegionPluginFragment | undefined | null
>
export type RegionPlugin = {
    regions: Ref<RegionFragmentMap>
    region: Readonly<Ref<RegionPluginFragment | undefined | null>>
    regionCode: Readonly<Ref<RegionCode>>
    selectRegion: SelectRegionFunction
    selectAndPersistRegion: SelectRegionFunction
    getCountryNameFromRegion(region: RegionCode): string | undefined
    routingRegion: Readonly<Ref<RoutingRegion>>
    hasRegionSet: Ref<boolean>
}

export const localeMapping: Record<RegionCode, Locale> = {
    INT: 'en-gb',
    UK: 'en-gb',
    EU: 'en-gb',
    US: 'en-us',
    IE: 'en-ie',
    NZ: 'en-nz',
    AU: 'en-au',
    CA: 'en-ca',
    ZA: 'en-za',
}

export const RegionSlugs: RoutingRegion[] = Object.values(RegionCode).map(
    (r) => r.toLowerCase() as RoutingRegion
)

export function installRegionPlugin(
    app: App,
    options: {
        getCountryName: AppTranslator['getCountryName']
        setLocale: AppTranslator['setLocale']
        cookies: ReactiveCookiePlugin
        region: RegionCode
    }
): RegionPlugin {
    const regions: RegionPlugin['regions'] = ref(
        Object.fromEntries(
            Object.entries(RegionCode).map((regionCode) => [
                regionCode,
                undefined,
            ])
        )
    )

    const regionCode = ref<RegionCode>(options.region)

    const region = toRef(() => regions.value[regionCode.value])

    const selectRegion: SelectRegionFunction = async (r) => {
        regionCode.value = r

        return await options.setLocale(localeMapping[r])
    }

    const { cookies } = options
    const { createRegionCookie } = cookies
    const regionCookie = createRegionCookie()

    const hasRegionSet = ref(regionCookie.value !== undefined)

    const selectAndPersistRegion: SelectRegionFunction = async (regionCode) => {
        return selectRegion(regionCode).then(() => {
            regionCookie.value = getMonolithLegacyRegionCookieValue(regionCode)
            hasRegionSet.value = true
        })
    }

    const routingRegion: ComputedRef<RoutingRegion> = computed(
        (): RoutingRegion => {
            return getRoutingRegion(regionCode.value)
        }
    )

    const pluginData: RegionPlugin = {
        regions,
        routingRegion,
        region,
        regionCode,
        selectRegion,
        selectAndPersistRegion,
        getCountryNameFromRegion(regionCode: RegionCode): string | undefined {
            if (regionCode === 'INT') {
                // TODO: translate - or maybe add names in the API ?
                return 'International'
            }

            return options.getCountryName(regionCode)
        },
        hasRegionSet,
    }
    const plugin: RegionPlugin = Object.freeze(pluginData)

    app.provide(pluginKey, plugin)

    return plugin
}

export function useRegionPlugin(): RegionPlugin {
    return inject(pluginKey) as RegionPlugin
}

export function getLocaleForRegion(r: RegionCode): Locale {
    return localeMapping[r]
}

export function isAppRegion(region: string): region is RegionCode {
    return includes(Object.values(RegionCode), region)
}

export function isMonolithLegacyRegionCookieValue(
    maybeLegacyRegion: string
): maybeLegacyRegion is MonolithLegacyRegionCookie {
    const upper = maybeLegacyRegion.toUpperCase()

    if (!isAppRegion(upper)) {
        return false
    }

    return maybeLegacyRegion === upper.toLowerCase()
}

export function getMonolithLegacyRegionCookieValue(
    regionCode: RegionCode
): MonolithLegacyRegionCookie {
    return regionCode.toLowerCase() as MonolithLegacyRegionCookie
}

export function isRoutingRegion(region: string): region is RoutingRegion {
    if ('' === region) {
        // noPrefixRegion
        return true
    }

    const upper = region.toUpperCase()

    if (!isAppRegion(upper)) {
        return false
    }

    const lower = region.toLowerCase()

    if (region !== lower) {
        // "Int" is not a routing region. "int" is.
        return false
    }

    return upper !== noPrefixRegion
}

// We need satisfies in order to have TS evaluate their types as literals
export const noPrefixRegion = 'UK' satisfies RegionCode
// We need satisfies in order to have TS evaluate their types as literals
export const defaultRegion = 'INT' satisfies RegionCode

export function getRoutingRegion(regionCode: RegionCode): RoutingRegion {
    if (noPrefixRegion === regionCode) {
        return ''
    }

    return regionCode.toLowerCase() as RoutingRegion
}

export function useRegionSelectOptions(): ComputedRef<
    SelectOption<RegionPluginFragment>[]
> {
    const { regions, getCountryNameFromRegion } = useRegionPlugin()

    return computed((): SelectOption<RegionPluginFragment>[] => {
        return Object.entries(regions.value)
            .map(([, region]) => {
                if (region?.isActive) {
                    return {
                        label:
                            getCountryNameFromRegion(region.code) ||
                            region.code,
                        value: region,
                    }
                }

                return undefined
            })
            .filter(isNotUndefinedOrNull)
    })
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    describe('isMonolithLegacyRegionCookieValue', () => {
        test('Good one', () => {
            expect(isMonolithLegacyRegionCookieValue('eu')).toBe(true)
        })
        test('Bad one', () => {
            expect(isMonolithLegacyRegionCookieValue('Eu')).toBe(false)
        })
        test('Bad one 2', () => {
            expect(isMonolithLegacyRegionCookieValue('UK')).toBe(false)
        })
        test('Good one', () => {
            expect(isMonolithLegacyRegionCookieValue('eu')).toBe(true)
        })
        test('Uk albeit no prefix region must be there with contents uk lower case', () => {
            expect(isMonolithLegacyRegionCookieValue('uk')).toBe(true)
        })
    })
}
